import { BrandIntelligence, Brand, CreativeSystem, MarketIntelligence, CompetitiveIntel, AudienceIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Style Union",
  industry: ["Clothing", "Retail Apparel and Fashion"],
  sub_industry: ["Value-fashion retail", "Fast fashion for men, women, and kids"],
  founded_year: 2022,
  founded_original_name: "Style Union",
  headquarters: ["Mumbai, Maharashtra, India"],
  tagline_primary: "Never Blend In",
  mission: '"Putting customers first. We help you build a wardrobe that suits your style and feel great in what you wear."',
  vision: '"To become India\'s leading value-fashion destination across tier 1, 2, 3, and 4 cities."',
  overview:
    "Style Union is an Indian fashion brand offering trendy, accessible clothing for women, men, and kids. It operates 140+ stores with value pricing and weekly launches, targeting young fashion-seekers in tier 2-4 cities.",
  scale: {
    revenue: 588.0,
    employees_estimate: 2550,
    markets_served_count: 1,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "India (National Operations)",
        headquarters: "Mumbai, Maharashtra, India",
        active_markets: [
          "Karnataka (Bengaluru)",
          "Telangana (Hyderabad, Karimnagar)",
          "Tamil Nadu (Thanjavur)",
          "Kerala (Kottayam)",
          "Maharashtra (Mumbai, Pune)",
          "Gujarat (Vadodara, Ahmedabad)",
          "Andhra Pradesh (Kakinada, Vizag)",
        ],
        market_type: "Owned retail stores (company-operated stores) and online direct-to-consumer",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "India (Karnataka - Bengaluru region)",
        share_percent: 0.0,
        notes:
          "Largest market concentration based on store count; the brand is focused on serving India's fashion customers in the T1 cities like Bangalore and Hyderabad which have established customer bases",
      },
      {
        region: "India (Telangana - Hyderabad region)",
        share_percent: 0.0,
        notes: "Second major market; significant store presence",
      },
      {
        region: "India (Maharashtra - Mumbai/Pune region)",
        share_percent: 0.0,
        notes: "Home market with headquarters presence",
      },
      {
        region: "India (Tamil Nadu region)",
        share_percent: 0.0,
        notes: "Growing presence in South India",
      },
      {
        region: "India (Gujarat - Ahmedabad/Vadodara region)",
        share_percent: 0.0,
        notes: "Western India market",
      },
      {
        region: "India (Tier 2/3/4 cities nationwide)",
        share_percent: 0.0,
        notes: "Core target market - customers aged 18-25 living in tier 2, 3 & 4 cities",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Bittersweet Coral",
          hex: "#FF6D6D",
        },
        {
          name: "Black",
          hex: "#000000",
        },
        {
          name: "White",
          hex: "#FFFFFF",
        },
      ],
      secondary: [],
      accent: [
        {
          name: "Bittersweet Coral",
          hex: "#FF6D6D",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Regular, Medium, Bold",
        usage: "Headlines, navigation, body text",
      },
      {
        font_family: "Sans-serif secondary",
        weight: "Medium, Bold",
        usage: "Buttons, labels, pricing",
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://styleunion.in/cdn/shop/files/style_union_logo_d67a9003-5dd4-40cd-b8ef-4ff71db2bba0.svg?v=1754924140&width=1500",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Youthful", "Empowering", "Accessible", "Confident", "Inclusive", "Trend-forward", "Honest"],
    taglines: ["Never Blend In", "Be the trendin'. Don't ever blend in.", "Find your voice. Find your song.", "Find your power. It's now or never.", "The world follows, only your style."],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: [],
  },
  creative_assets: ["https://styleunion.in/cdn/shop/files/style_union_logo_d67a9003-5dd4-40cd-b8ef-4ff71db2bba0.svg?v=1754924140&width=1500"],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Style Union"],
    selected_brand: "Style Union",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "High",
      patterns_learned: 100,
      avg_generation: "High",
    },
    style_drift_alerts: [
      {
        title: "Style Union Logo",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
    ],
    preference_learning: [
      {
        preference: "Style Union",
        score: 90,
        change: 10,
      },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.8,
    sentiment_graph: [
      {
        metric: "Customer Satisfaction Rating",
        value: "4.8/5.0",
        delta: "+0.3",
      },
      {
        metric: "Store Network Growth",
        value: "140+ stores",
        delta: "+71%",
      },
      {
        metric: "Revenue Growth",
        value: "₹588 Cr",
        delta: "+85%",
      },
      {
        metric: "Market Penetration (Tier 2-4)",
        value: "~2.5%",
        delta: "+1.2%",
      },
      {
        metric: "Inventory Turnover Ratio",
        value: "4x",
        delta: "Industry-leading",
      },
      {
        metric: "Weekly Collection Refresh Rate",
        value: "100%",
        delta: "New metric",
      },
      {
        metric: "Customer Base",
        value: "5M+",
        delta: "+180%",
      },
      {
        metric: "Employee Growth",
        value: "2,550",
        delta: "+95%",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["February 2025", "February 2026"],
    themes: {
      positive: [
        "Affordable Pricing & Value for Money",
        "Wide Product Variety",
        "Helpful & Friendly Staff",
        "Good Fabric Quality",
        "Trendy & Fresh Designs",
        "Convenient Store Locations",
        "Comfortable Fit",
      ],
      neutral: ["Return Policy Awareness", "Online vs Offline Experience", "Seasonal Stock Availability", "Delivery Timelines"],
      negative: ["Quality Inconsistency", "Limited Size Range", "Color Fading Issues", "Coupon/Exchange Policy Confusion", "Stock-Out Issues", "Return Fee Perception"],
    },
  },
  distribution: [
    {
      brand: "Zudio (Tata Trent)",
      share: 18.5,
    },
    {
      brand: "Max Fashion (Landmark Group)",
      share: 12.8,
    },
    {
      brand: "Pantaloons (ABFRL)",
      share: 11.2,
    },
    {
      brand: "Westside (Tata Trent)",
      share: 9.5,
    },
    {
      brand: "Others",
      share: 48.0,
    },
  ],
  sentiment_by_product: [
    {
      product: "Women's Jeans",
      sentiment: 8.2,
      change_delta: 5.0,
    },
    {
      product: "Women's Kurtas & Ethnic Wear",
      sentiment: 8.5,
      change_delta: 8.0,
    },
    {
      product: "Men's Jeans & Chinos",
      sentiment: 7.9,
      change_delta: 3.0,
    },
    {
      product: "Men's T-shirts & Polos",
      sentiment: 7.6,
      change_delta: 2.0,
    },
    {
      product: "Women's Tops & Dresses",
      sentiment: 8.1,
      change_delta: 6.0,
    },
    {
      product: "Kids' Clothing",
      sentiment: 7.8,
      change_delta: 4.0,
    },
    {
      product: "Athleisure & Loungewear",
      sentiment: 8.3,
      change_delta: 10.0,
    },
    {
      product: "Accessories",
      sentiment: 7.4,
      change_delta: 1.0,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Affordable & Transparent Pricing",
        weight: 10.0,
      },
      {
        topic: "Helpful Store Staff",
        weight: 9.0,
      },
      {
        topic: "Wide Product Variety",
        weight: 9.0,
      },
      {
        topic: "Good Fabric Quality",
        weight: 8.0,
      },
      {
        topic: "Trendy & Fresh Designs",
        weight: 8.0,
      },
      {
        topic: "Comfortable Fit",
        weight: 7.0,
      },
      {
        topic: "Accessible Locations",
        weight: 6.0,
      },
    ],
    complaints: [
      {
        topic: "Quality Inconsistency",
        weight: 6.0,
      },
      {
        topic: "Limited Size Range",
        weight: 5.0,
      },
      {
        topic: "Return Fee & Policy Confusion",
        weight: 5.0,
      },
      {
        topic: "Color Fading Issues",
        weight: 4.0,
      },
      {
        topic: "Stock Availability",
        weight: 4.0,
      },
      {
        topic: "Delivery & Courier Issues",
        weight: 3.0,
      },
      {
        topic: "Limited Winter Collection",
        weight: 2.0,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Zudio",
      threat_level: "high",
      market_share: 13.8,
      sentiment: 85.0,
    },
    {
      name: "Reliance Trends",
      threat_level: "high",
      market_share: 16.5,
      sentiment: 78.0,
    },
    {
      name: "Max Fashion",
      threat_level: "high",
      market_share: 9.0,
      sentiment: 75.0,
    },
    {
      name: "Pantaloons",
      threat_level: "medium-high",
      market_share: 7.0,
      sentiment: 72.0,
    },
    {
      name: "Westside",
      threat_level: "medium",
      market_share: 3.8,
      sentiment: 70.0,
    },
    {
      name: "Bewakoof",
      threat_level: "medium",
      market_share: 2.5,
      sentiment: 80.0,
    },
    {
      name: "H&M India",
      threat_level: "medium",
      market_share: 4.5,
      sentiment: 68.0,
    },
    {
      name: "NewMe",
      threat_level: "low-medium",
      market_share: 1.5,
      sentiment: 75.0,
    },
  ],
  secondary_competitors: [
    {
      name: "Lifestyle",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "Shoppers Stop",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "FabAlley",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "V-Mart",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "Myntra",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
    {
      name: "AJIO",
      threat_level: "medium",
      market_share: 0.0,
      sentiment: 0.0,
    },
  ],
  share_of_voice: [
    {
      channel: "Social Media",
      distribution: {
        Bewakoof: 27.0,
        "Reliance Trends": 17.0,
        "Max Fashion": 13.0,
        Pantaloons: 11.0,
        Zudio: 9.0,
        "Style Union": 4.0,
        Others: 19.0,
      },
    },
    {
      channel: "Digital Marketing",
      distribution: {
        Bewakoof: 27.0,
        "Reliance Trends": 17.0,
        "Max Fashion": 13.0,
        Pantaloons: 11.0,
        Zudio: 9.0,
        "Style Union": 19.0,
        Others: 4.0,
      },
    },
    {
      channel: "Traditional Marketing",
      distribution: {
        Bewakoof: 27.0,
        "Reliance Trends": 17.0,
        "Max Fashion": 13.0,
        Pantaloons: 11.0,
        Zudio: 9.0,
        "Style Union": 19.0,
        Others: 4.0,
      },
    },
    {
      channel: "Direct Sales",
      distribution: {
        Bewakoof: 27.0,
        "Reliance Trends": 17.0,
        "Max Fashion": 13.0,
        Pantaloons: 11.0,
        Zudio: 9.0,
        "Style Union": 19.0,
        Others: 4.0,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Net Promoter Score (NPS)",
      brands: [
        {
          brand_name: "Zudio",
          score: 86,
        },
        {
          brand_name: "Bewakoof",
          score: 85,
        },
        {
          brand_name: "Reliance Trends",
          score: 82,
        },
        {
          brand_name: "Max Fashion",
          score: 81,
        },
        {
          brand_name: "Pantaloons",
          score: 79,
        },
        {
          brand_name: "Westside",
          score: 78,
        },
        {
          brand_name: "H&M India",
          score: 80,
        },
        {
          brand_name: "Style Union",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Brand Awareness",
      brands: [
        {
          brand_name: "Reliance Trends",
          score: 85,
        },
        {
          brand_name: "Pantaloons",
          score: 80,
        },
        {
          brand_name: "Zudio",
          score: 75,
        },
        {
          brand_name: "Westside",
          score: 72,
        },
        {
          brand_name: "Max Fashion",
          score: 68,
        },
        {
          brand_name: "H&M India",
          score: 78,
        },
        {
          brand_name: "Bewakoof",
          score: 55,
        },
        {
          brand_name: "Style Union",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Score (CSAT)",
      brands: [
        {
          brand_name: "Zudio",
          score: 82,
        },
        {
          brand_name: "Bewakoof",
          score: 78,
        },
        {
          brand_name: "Reliance Trends",
          score: 75,
        },
        {
          brand_name: "Max Fashion",
          score: 73,
        },
        {
          brand_name: "Pantaloons",
          score: 70,
        },
        {
          brand_name: "H&M India",
          score: 72,
        },
        {
          brand_name: "Westside",
          score: 68,
        },
        {
          brand_name: "Style Union",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Brand Trust Score",
      brands: [
        {
          brand_name: "Zudio",
          score: 88,
        },
        {
          brand_name: "Reliance Trends",
          score: 85,
        },
        {
          brand_name: "Westside",
          score: 82,
        },
        {
          brand_name: "Pantaloons",
          score: 75,
        },
        {
          brand_name: "Max Fashion",
          score: 72,
        },
        {
          brand_name: "H&M India",
          score: 80,
        },
        {
          brand_name: "Bewakoof",
          score: 65,
        },
        {
          brand_name: "Style Union",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Value for Money Perception",
      brands: [
        {
          brand_name: "Zudio",
          score: 92,
        },
        {
          brand_name: "V-Mart",
          score: 88,
        },
        {
          brand_name: "Reliance Trends",
          score: 85,
        },
        {
          brand_name: "Max Fashion",
          score: 82,
        },
        {
          brand_name: "Style Union",
          score: 0,
        },
        {
          brand_name: "Pantaloons",
          score: 75,
        },
        {
          brand_name: "Westside",
          score: 68,
        },
        {
          brand_name: "H&M India",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Store Experience Quality",
      brands: [
        {
          brand_name: "Zudio",
          score: 85,
        },
        {
          brand_name: "Westside",
          score: 82,
        },
        {
          brand_name: "H&M India",
          score: 80,
        },
        {
          brand_name: "Reliance Trends",
          score: 78,
        },
        {
          brand_name: "Max Fashion",
          score: 75,
        },
        {
          brand_name: "Pantaloons",
          score: 72,
        },
        {
          brand_name: "Bewakoof",
          score: 0,
        },
        {
          brand_name: "Style Union",
          score: 0,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [],
    improvement_areas: [],
    disruption_watchlist: ["The Souled Store", "Urbanic", "Newme", "Shein India", "Citykart"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Targeting customers in the ages between 18-25 living in tier 2, 3 & 4 cities, Style Union meets their demand for trendy and affordable fashion by bringing in new styles every week. The brand's audience is characterized by desire to stay ahead of their peers despite hailing from smaller cities and humble economic backgrounds, with the tagline 'Never Blend In' crafted as a tribute to this audience.",
  core_demographics: {
    primary_age_range: "18-25",
    gender_split: {
      female: 52.0,
      male: 48.0,
    },
    demographics_distribution: {
      age: [
        {
          age_range: "18-25",
          percentage: 70,
        },
        {
          age_range: "26-34",
          percentage: 20,
        },
        {
          age_range: "35-44",
          percentage: 10,
        },
      ],
      education: [
        {
          education_level: "High School",
          percentage: 52.5,
        },
        {
          education_level: "Bachelor's Degree",
          percentage: 38.5,
        },
        {
          education_level: "Master's Degree",
          percentage: 10,
        },
      ],
      geographic: [
        {
          location: "Urban",
          percentage: 50,
        },
        {
          location: "Suburban",
          percentage: 30,
        },
        {
          location: "Rural",
          percentage: 20,
        },
      ],
    },
    income_median_usd: 4200,
    location_type: "Urban",
  },
  occupation_distribution: [
    {
      occupation: "Students",
      percentage: 52.0,
    },
    {
      occupation: "Working Professionals",
      percentage: 30,
    },
    {
      occupation: "Homemakers",
      percentage: 18,
    },
  ],
  interests: {
    top_categories: [
      {
        category: "Fashion & Style",
        score: 9.0,
      },
      {
        category: "Social Media & Content Creation",
        score: 9.0,
      },
      {
        category: "Bollywood & Entertainment",
        score: 8.0,
      },
      {
        category: "Music & Pop Culture",
        score: 8.0,
      },
      {
        category: "Mobile Gaming",
        score: 7.0,
      },
      {
        category: "Fitness & Wellness",
        score: 7.0,
      },
      {
        category: "Food & Dining Out",
        score: 7.0,
      },
      {
        category: "Travel & Exploration",
        score: 6.0,
      },
      {
        category: "Technology & Gadgets",
        score: 6.0,
      },
      {
        category: "Career Development",
        score: 6.0,
      },
    ],
    brand_affinities: [
      {
        brand: "Bewakoof",
        affinity: 42.0,
      },
      {
        brand: "Myntra",
        affinity: 38.0,
      },
      {
        brand: "H&M",
        affinity: 28.0,
      },
      {
        brand: "Zara",
        affinity: 25.0,
      },
      {
        brand: "The Souled Store",
        affinity: 24.0,
      },
      {
        brand: "Ajio",
        affinity: 22.0,
      },
      {
        brand: "Snitch",
        affinity: 18.0,
      },
      {
        brand: "Flipkart Fashion",
        affinity: 35.0,
      },
      {
        brand: "Nykaa Fashion",
        affinity: 20.0,
      },
      {
        brand: "Rare Rabbit",
        affinity: 15.0,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Retail stores (offline)": 58.0,
        "Online (brand website/app)": 28.0,
        "E-commerce marketplaces": 14.0,
      },
      purchase_frequency: "3.2 times per year",
      avg_order_value_usd: 30.0,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Social Media Discovery",
          description: "Instagram is a prime channel for investigating new fashion trends; 39% consumers buying fashion products after watching Instagram reels",
          duration_days: 3,
        },
        {
          stage: 2,
          name: "Peer Validation & Browsing",
          description: "Check reviews, compare prices across platforms, seek peer opinions on social media",
          duration_days: 5,
        },
        {
          stage: 3,
          name: "In-Store or Online Evaluation",
          description: "Visit physical store or browse online catalog, check availability, evaluate fit and style",
          duration_days: 2,
        },
        {
          stage: 4,
          name: "Transaction & Checkout",
          description: "Complete purchase in-store or online with preferred payment method (UPI, COD, cards)",
          duration_days: 1,
        },
        {
          stage: 5,
          name: "Social Sharing & Advocacy",
          description: "Share purchases on social media, provide reviews, recommend to peers",
          duration_days: 7,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        Mobile: 72.0,
        Desktop: 18.0,
        Tablet: 6.0,
        "In-store (physical)": 4.0,
      },
      media_channels: {
        Instagram: 38.0,
        YouTube: 22.0,
        Facebook: 15.0,
        WhatsApp: 12.0,
        Snapchat: 5.0,
        "Twitter/X": 4.0,
        LinkedIn: 2.0,
        "Other (TikTok alternatives, regional apps)": 2.0,
      },
    },
  },
  platforms: {
    distribution: {
      "Retail stores (offline)": 58.0,
      "Online (brand website/app)": 28.0,
      "E-commerce marketplaces": 14.0,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 101000,
      health_score: 60.0,
    },
    {
      platform: "LinkedIn",
      followers: 1693,
      health_score: 50.0,
    },
    {
      platform: "Twitter/X",
      followers: 1000,
      health_score: 40.0,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 0.0,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0.0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0.0,
    },
    {
      platform: "Pinterest",
      followers: 0,
      health_score: 0.0,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 40.0,
    },
    {
      label: "Neutral",
      percentage: 40.0,
    },
    {
      label: "Negative",
      percentage: 20.0,
    },
  ],
  recent_comments: [
    {
      user: "fashionista_101",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Love the new collection! So trendy and affordable.",
      postContext: "Comment on a post showcasing the latest summer collection.",
    },
    {
      user: "style_guru",
      platform: "LinkedIn",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Interesting insights on fashion trends. Looking forward to more updates.",
      postContext: "Comment on a LinkedIn article about fashion industry trends.",
    },
    {
      user: "shopper123",
      platform: "Twitter/X",
      timeAgo: "3 days ago",
      status: "negative",
      commentText: "Disappointed with the customer service at the new store.",
      postContext: "Reply to a tweet about the opening of a new store location.",
    },
  ],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 1.2,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 75.0,
      },
      {
        platform: "Twitter/X",
        engagement_rate: 30.0,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Instagram Reels",
        performance: 9.0,
      },
      {
        content_type: "Carousel Posts",
        performance: 8.0,
      },
      {
        content_type: "Product Showcase Posts",
        performance: 6.0,
      },
      {
        content_type: "User-Generated Content (UGC)",
        performance: 7.0,
      },
      {
        content_type: "Behind-the-Scenes / Store Content",
        performance: 6.0,
      },
      {
        content_type: "Trend-Based Fashion Content",
        performance: 7.0,
      },
      {
        content_type: "Stories (Ephemeral Content)",
        performance: 6.0,
      },
    ],
    follower_growth_trend: [
      {
        month: "March 2024",
        followers: 85000.0,
      },
      {
        month: "February 2026",
        followers: 102693.0,
      },
    ],
  },
};

export const mBrandIntelligenceStyleUnion: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
